/**
 * #264 (02/10/2026) — EXISTENCIA na fonte, lida do feed CP do Sync.
 *
 * Dois fatos da serie de saidas que nao cabem numa barra:
 *  - BALDE `nao_encontrado_by_id`: titulos que a Omie confirmou POR ID que nao
 *    existem. Estao FORA das saidas e do saldo projetado — e ficam aqui a vista
 *    (n, valor, lista), em vez de desaparecerem.
 *  - `ausente_n_ciclos`: titulos ausentes da listagem da Omie, NAO confirmados por
 *    id. SEGUEM no saldo (ausencia nao chega para mexer em dinheiro); o bloco diz
 *    quantos, quanto e ha quantos dias.
 *
 * Estados: sem os campos (backend antigo) ou CP fora => nao renderiza (o aviso de
 * CP indisponivel ja e do grafico); n=0 nos dois => nao renderiza (nada a dizer);
 * literal desconhecido => dito, com o literal que chegou.
 */
import type { CpExistenciaItem, CpExistenciaResumo, CpNaoEncontradoBalde } from "../../api/types";
import { fmtBRL } from "../../utils/formatters";

export interface ExistenciaCpCardProps {
  balde: CpNaoEncontradoBalde | null;
  existencia: CpExistenciaResumo | null;
}

function _isoParaBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

function _lista(itens: CpExistenciaItem[], testid: string) {
  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-[11px] font-mono tabular-nums" data-testid={testid}>
        <thead className="text-white/50 text-left">
          <tr>
            <th className="pr-3 py-1 font-normal">credor</th>
            <th className="pr-3 py-1 font-normal">categoria</th>
            <th className="pr-3 py-1 font-normal">previsão</th>
            <th className="pr-3 py-1 font-normal text-right">dias ausente</th>
            <th className="pr-3 py-1 font-normal">última listagem</th>
            <th className="py-1 font-normal text-right">valor</th>
          </tr>
        </thead>
        <tbody className="text-white/80">
          {itens.map((i) => (
            <tr key={`${i.id_omie ?? ""}-${i.data_previsao}`} className="border-t border-white/5">
              <td className="pr-3 py-1 truncate max-w-[220px]" title={i.credor}>{i.credor}</td>
              <td className="pr-3 py-1 truncate max-w-[180px]" title={i.categoria}>{i.categoria}</td>
              <td className="pr-3 py-1">{_isoParaBR(i.data_previsao)}</td>
              <td className="pr-3 py-1 text-right">{i.dias_ausente ?? "—"}</td>
              <td className="pr-3 py-1">{_isoParaBR(i.ausente_na_fonte_desde)}</td>
              <td className="py-1 text-right">{fmtBRL(i.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ExistenciaCpCard({ balde, existencia }: ExistenciaCpCardProps) {
  const baldeOk = !!balde && balde.disponivel;
  const exOk = !!existencia && existencia.disponivel && !!existencia.classes;
  const nBalde = baldeOk ? (balde!.n ?? 0) : 0;
  const ausentes = exOk ? existencia!.classes!["ausente_n_ciclos"] : undefined;
  const nAusentes = ausentes?.n ?? 0;
  const literais = exOk ? existencia!.desconhecida_literais : [];
  if (nBalde === 0 && nAusentes === 0 && literais.length === 0) return null;

  return (
    <div
      className="bg-indra-azul-surface rounded-xl border border-white/10 p-4 mt-4"
      data-testid="existencia-cp-card"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h3 className="text-white font-semibold text-sm">Existência dos títulos na Omie</h3>
        <span className="text-[10px] text-white/40 uppercase tracking-wider ml-auto">
          CP vivo (Sync) · na janela do gráfico
        </span>
      </div>

      {nBalde > 0 && (
        <div data-testid="existencia-cp-balde">
          <div className="flex items-baseline gap-3 flex-wrap font-mono tabular-nums">
            <span className="text-[#FF6B6B] font-semibold text-sm" data-testid="existencia-cp-balde-n">
              {nBalde} {nBalde === 1 ? "título" : "títulos"} fora do saldo
            </span>
            <span className="text-white/90 text-sm" data-testid="existencia-cp-balde-valor">
              {fmtBRL(balde!.valor ?? 0)}
            </span>
            <span className="text-white/50 text-[11px] cursor-help" title={balde!.caveat}>
              a Omie confirmou por id que não estão cadastrados — não somam nas saídas
            </span>
          </div>
          <details className="mt-1">
            <summary className="cursor-pointer text-[11px] text-indra-turquesa/80 hover:text-indra-turquesa select-none">
              ver os {nBalde} fora do saldo
            </summary>
            {_lista(balde!.itens, "existencia-cp-balde-lista")}
          </details>
        </div>
      )}

      {nAusentes > 0 && (
        <div className={nBalde > 0 ? "mt-3" : ""} data-testid="existencia-cp-ausentes">
          <div className="flex items-baseline gap-3 flex-wrap font-mono tabular-nums">
            <span className="text-amber-200/90 font-semibold text-sm" data-testid="existencia-cp-ausentes-n">
              {nAusentes} {nAusentes === 1 ? "título ausente" : "títulos ausentes"} da listagem — seguem no saldo
            </span>
            <span className="text-white/90 text-sm" data-testid="existencia-cp-ausentes-valor">
              {fmtBRL(ausentes!.valor)}
            </span>
            <span className="text-white/50 text-[11px] cursor-help" title={existencia!.rotulo_ausente_n_ciclos}>
              ausência na listagem não é confirmação: continuam nas saídas até a Omie responder por id
            </span>
          </div>
          <details className="mt-1">
            <summary className="cursor-pointer text-[11px] text-indra-turquesa/80 hover:text-indra-turquesa select-none">
              ver os {nAusentes} ausentes
            </summary>
            {_lista(existencia!.ausente_n_ciclos_itens, "existencia-cp-ausentes-lista")}
          </details>
        </div>
      )}

      {literais.length > 0 && (
        <div className="mt-2 text-[11px] text-amber-200/90" data-testid="existencia-cp-desconhecida">
          valor de existência não catalogado no feed: {literais.join(", ")} — esses títulos seguem no saldo
        </div>
      )}
    </div>
  );
}
